/**
 * The member note composer: subject, text, the org's categories, staged files.
 *
 * Lifted out of MemberActivityFeed (TRUS-762) so the member's Activities
 * workspace can open "Add → Note" as a screen of its own, while the enrollment
 * page and note sections keep the composer inline above their feed. One
 * component, so a note written from either place is saved the same way.
 *
 * Files are only STAGED here: an attachment hangs off a note_id, so they upload
 * once the note exists (TRUS-520), sequentially so a partial failure leaves the
 * files that did upload attached.
 */
import React, { useState } from 'react';
import { Loader2, Paperclip, Save, X } from 'lucide-react';

import { Button } from '../ui/button';
import { ACCENT_BUTTON } from '../ui/accent';
import { memberActivityFeedAPI, type NoteCategory } from '@/services/api/memberActivityFeed';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { getApiErrorMessage } from '@/utils/apiErrors';

export interface MemberNoteComposerProps {
  memberId: number;
  /** Attaches the note to this enrollment; otherwise the API picks the member's current one. */
  enrollmentId?: number;
  /** The note is filed under this case (TRUS-762). */
  caseEpisodeId?: number | null;
  /** The org's categories; only active ones are offered. */
  categories: NoteCategory[];
  onSaved: () => void;
  onCancel: () => void;
}

export default function MemberNoteComposer({
  memberId,
  enrollmentId,
  caseEpisodeId,
  categories,
  onSaved,
  onCancel,
}: MemberNoteComposerProps) {
  const { t } = useAppTranslation();
  const [draft, setDraft] = useState('');
  const [draftSubject, setDraftSubject] = useState('');
  const [draftCategories, setDraftCategories] = useState<number[]>([]);
  const [draftFiles, setDraftFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeCategories = categories.filter((c) => c.is_active);
  const toggle = (id: number) =>
    setDraftCategories((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const created = await memberActivityFeedAPI.createNote(memberId, {
        note_text: draft.trim(),
        subject: draftSubject.trim() || undefined,
        enrollment_id: enrollmentId,
        category_ids: draftCategories,
        ...(caseEpisodeId ? { case_episode_id: caseEpisodeId } : {}),
      });
      if (draftFiles.length && created?.id) {
        for (const file of draftFiles) {
          await memberActivityFeedAPI.uploadNoteAttachment(created.id, file);
        }
      }
      setDraft('');
      setDraftSubject('');
      setDraftCategories([]);
      setDraftFiles([]);
      onSaved();
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('activity.saveFailed', 'Could not save the note.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-v2border bg-surface p-3 space-y-2">
      <input
        value={draftSubject}
        onChange={(e) => setDraftSubject(e.target.value)}
        placeholder={t('activity.subjectOptional', 'Subject (optional)')}
        className="w-full text-sm rounded-md border border-v2border bg-surface px-2 py-1.5"
      />
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={5}
        placeholder={t('activity.notePlaceholder', 'What happened?')}
        className="w-full text-sm rounded-md border border-v2border bg-surface px-2 py-1.5 resize-y min-h-[120px] leading-relaxed"
      />
      {activeCategories.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {activeCategories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => toggle(c.id)}
              aria-pressed={draftCategories.includes(c.id)}
              className={`px-2 py-1 text-xs rounded border transition-colors ${
                draftCategories.includes(c.id)
                  ? 'bg-v2accent text-white border-v2accent'
                  : 'border-v2border text-ink-500 hover:text-ink'
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-1.5 text-xs text-ink-500 hover:text-ink cursor-pointer">
          <Paperclip className="w-3.5 h-3.5" />
          {t('activity.attachFile', 'Attach a file')}
          <input
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              setDraftFiles((prev) => [...prev, ...Array.from(e.target.files || [])]);
              // Clear the input so re-picking the same file still fires onChange.
              e.target.value = '';
            }}
          />
        </label>
        {draftFiles.map((f, i) => (
          <span
            key={`${f.name}-${i}`}
            className="inline-flex items-center gap-1 rounded border border-v2border px-2 py-0.5 text-xs text-ink-500"
          >
            {f.name}
            <button
              type="button"
              aria-label={t('activity.removeFile', 'Remove file')}
              onClick={() => setDraftFiles((prev) => prev.filter((_, j) => j !== i))}
              className="hover:text-red-600"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {draftFiles.length > 0 && (
          <span className="text-[11px] text-ink-400">
            {t('activity.attachesOnSave', 'attaches when you save the note')}
          </span>
        )}
      </div>
      {/* A staged file with no note text cannot be saved (the API requires
          note_text); say why rather than leave a dead button (TRUS-553). */}
      {draftFiles.length > 0 && !draft.trim() && (
        <p className="text-xs text-amber-600 dark:text-amber-500">
          {t(
            'activity.fileNeedsNote',
            'Add a note before saving — the file attaches to the note, so it cannot be saved on its own.',
          )}
        </p>
      )}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/30 dark:border-red-900 dark:text-red-300">
          {error}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Button size="sm" className={ACCENT_BUTTON} onClick={save} disabled={saving || !draft.trim()}>
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          {t('activity.saveNote', 'Save note')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>
          {t('common.cancel', 'Cancel')}
        </Button>
      </div>
    </div>
  );
}
